import type { CSSProperties, FC, ReactNode } from 'react'
import type { PackageArt } from '@/types'

import Image from 'next/image'
import { cn } from '@/lib/utils'
import { darken, lighten, safeHex } from '@/lib/color'
import { LOGO_HEIGHT, LOGO_SRC, LOGO_WIDTH } from '@/constants/site'

const TEETH = 14
const DEPTH = 1.8

const ZIGZAG = (() => {
    const top: Array<string> = []
    const bottom: Array<string> = []

    for (let i = 0; i <= TEETH * 2; i++) top.push(`${((i / (TEETH * 2)) * 100).toFixed(2)}% ${i % 2 === 0 ? 0 : DEPTH}%`)
    for (let i = TEETH * 2; i >= 0; i--) bottom.push(`${((i / (TEETH * 2)) * 100).toFixed(2)}% ${i % 2 === 0 ? 100 : 100 - DEPTH}%`)

    return `polygon(${[...top, ...bottom].join(', ')})`
})()

interface PackageFallbackProps {
    art: PackageArt
    className?: string
    cmsId?: string
    showName?: boolean
}

/* A flat illustrated pouch: shown while 3D loads and on devices without WebGL */
export const PackageFallback: FC<PackageFallbackProps> = ({ art, className, cmsId, showName = true }): ReactNode => {
    const pkg = safeHex(art.packageColor, '#F7B32B')
    const accent = safeHex(art.accentColor, '#6B3410')
    const seal: CSSProperties = { background: `repeating-linear-gradient(90deg, ${darken(pkg, 0.16)} 0 3px, ${darken(pkg, 0.06)} 3px 6px)` }

    return (
        <div className={cn('flex items-center justify-center', className)} aria-hidden='true'>
            <div className='@container relative aspect-[2/3] h-[76%] max-w-[64%]' style={{ filter: 'drop-shadow(0 16px 14px rgba(59, 31, 14, 0.26))' }}>
                <div
                    className='absolute inset-0 overflow-hidden rounded-[6%]'
                    style={{ clipPath: ZIGZAG, background: `linear-gradient(180deg, ${lighten(pkg, 0.25)} 0%, ${pkg} 46%, ${darken(pkg, 0.18)} 100%)` }}
                >
                    <div className='absolute inset-x-0 top-0 h-[7.5%]' style={seal} />
                    <div className='absolute inset-x-0 bottom-0 h-[7.5%]' style={seal} />
                    <div className='absolute inset-y-[9%] left-[9%] w-[14%] rounded-full bg-white/25 blur-[2px]' />

                    <div className='absolute left-1/2 top-[10%] w-[34%] -translate-x-1/2 -rotate-3 rounded-[12%] bg-white p-[3%] shadow-md'>
                        <Image src={LOGO_SRC} alt='' width={LOGO_WIDTH} height={LOGO_HEIGHT} sizes='96px' className='h-auto w-full' />
                    </div>

                    <div className='absolute inset-x-0 top-[44%] flex h-[16%] items-center justify-center px-[7%]' style={{ background: accent }}>
                        {showName && (
                            <span
                                className='line-clamp-2 text-center font-heading font-bold leading-[1.05] text-brand-cream'
                                style={{ fontSize: '9.5cqw', textShadow: `0 0.08em 0 ${darken(accent, 0.45)}` }}
                                data-mdlfy-cms={cmsId ? `products:${cmsId}:name` : undefined}
                            >
                                {art.name}
                            </span>
                        )}
                    </div>

                    <div
                        className='absolute left-1/2 top-[63%] aspect-square w-[46%] -translate-x-1/2 rounded-full border-[3px] border-white'
                        style={{
                            boxShadow: `0 0 0 3px ${accent}`,
                            background: `radial-gradient(circle at 30% 35%, #FFF9EA 0 12%, transparent 13%), radial-gradient(circle at 62% 48%, #F4D27A 0 16%, transparent 17%), radial-gradient(circle at 40% 68%, #E9BD62 0 15%, transparent 16%), radial-gradient(circle at 70% 25%, #F0C96E 0 12%, transparent 13%), radial-gradient(circle at 50% 45%, #FFF6DD, #F8DDA4)`
                        }}
                    />
                </div>
            </div>
        </div>
    )
}
